import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Languages, Menu, Moon, Sun, X } from "lucide-react";
import { usePreferences } from "../PreferencesContext";
import profilePhoto from "../assets/images/profile-photo.webp";
import { cn } from "../utils/cn";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const toggleRef = useRef(null);
  const navRef = useRef(null);
  const { copy, language, theme, toggleLanguage, toggleTheme } = usePreferences();

  const navItems = [
    { id: "about", label: copy.nav.about },
    { id: "expertise", label: copy.nav.expertise },
    { id: "projects", label: copy.nav.projects },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = "hidden";

    const focusable = navRef.current?.querySelectorAll("a, button:not([disabled])") || [];
    focusable[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        requestAnimationFrame(() => toggleRef.current?.focus());
        return;
      }

      if (event.key !== "Tab" || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-[100] px-[var(--gutter)] py-[0.8rem] max-[720px]:py-4">
      <div className={cn(
        "pointer-events-auto mx-auto flex min-h-14 w-[min(100%,var(--container))] items-center justify-between rounded-full border border-transparent px-[0.4rem] py-[0.35rem] pl-2 transition-[background-color,box-shadow,border-color] duration-200",
        "max-[720px]:min-h-[3.25rem] max-[720px]:max-w-none max-[720px]:p-0",
        scrolled && !menuOpen && "header-glass max-w-[68rem] border-white/70 bg-[var(--glass)] shadow-[0_0.75rem_2.25rem_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.9)] backdrop-blur-[24px] backdrop-saturate-[1.8] dark:border-white/10 dark:shadow-[0_0.75rem_2.25rem_rgba(0,0,0,0.42),inset_0_1px_0_rgba(255,255,255,0.08)] max-[720px]:border-transparent max-[720px]:bg-transparent max-[720px]:shadow-none max-[720px]:backdrop-blur-none max-[720px]:backdrop-saturate-100 dark:max-[720px]:border-transparent dark:max-[720px]:shadow-none",
      )}>
        <a className="inline-flex min-h-11 items-center gap-[0.7rem] text-[var(--ink)] no-underline max-[720px]:relative max-[720px]:z-30" href="#home" aria-label={`Mohammadamin Vaez — ${copy.footer.top}`} onClick={closeMenu}>
          <img
            className="size-9 shrink-0 rounded-full border border-[color-mix(in_srgb,var(--ink)_12%,transparent)] object-cover object-[50%_34%] shadow-[0_0_0_2px_color-mix(in_srgb,var(--page)_86%,transparent),0_0.35rem_1rem_rgba(0,0,0,0.2)] max-[720px]:size-[3.25rem]"
            src={profilePhoto}
            alt=""
            aria-hidden="true"
            width="96"
            height="96"
          />
          <span className="text-[0.83rem] font-[650] max-[720px]:hidden">Mohammadamin Vaez</span>
        </a>

        <button
          className="mobile-menu-toggle hidden size-11 place-items-center rounded-full border-0 bg-black/[0.055] text-[var(--ink)] transition-transform duration-150 ease-out active:scale-95 max-[720px]:relative max-[720px]:z-30 max-[720px]:grid max-[720px]:size-[3.25rem] max-[720px]:border max-[720px]:border-white/80 max-[720px]:bg-[color-mix(in_srgb,var(--surface)_96%,transparent)] max-[720px]:shadow-lg max-[720px]:backdrop-blur-xl max-[720px]:backdrop-saturate-150 dark:max-[720px]:border-white/10 dark:max-[720px]:shadow-black/40 max-[720px]:[&_svg]:size-[1.3rem]"
          ref={toggleRef}
          type="button"
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          aria-label={menuOpen ? copy.nav.close : copy.nav.open}
          onClick={() => setMenuOpen((current) => !current)}
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>

        <nav
          id="primary-navigation"
          ref={navRef}
          aria-label={copy.nav.label}
          className={cn(
            "mobile-nav flex items-center gap-7",
            "max-[720px]:fixed max-[720px]:inset-0 max-[720px]:z-20 max-[720px]:hidden max-[720px]:min-h-dvh max-[720px]:origin-top-right max-[720px]:flex-col max-[720px]:items-stretch max-[720px]:justify-end max-[720px]:gap-4 max-[720px]:border-0 max-[720px]:rounded-none max-[720px]:bg-[rgba(245,245,247,0.7)] max-[720px]:px-[var(--gutter)] max-[720px]:pt-[8.5rem] max-[720px]:pb-[max(1.5rem,env(safe-area-inset-bottom))] max-[720px]:backdrop-blur-[24px] max-[720px]:backdrop-saturate-150 dark:max-[720px]:bg-[rgba(7,7,8,0.7)]",
            menuOpen && "primary-nav--open max-[720px]:flex",
          )}
        >
          <div className="flex items-center gap-1 max-[720px]:flex-1 max-[720px]:flex-col max-[720px]:items-stretch max-[720px]:justify-center max-[720px]:gap-[0.15rem]">
            {navItems.map((item) => (
              <a
                className="mobile-nav-link group inline-flex min-h-11 items-center gap-[0.45rem] rounded-full px-3 py-[0.82rem] text-[0.78rem] font-[550] text-[var(--muted)] no-underline transition-colors duration-150 hover:bg-[color-mix(in_srgb,var(--ink)_5%,transparent)] hover:text-[var(--ink)] max-[720px]:relative max-[720px]:min-h-[4.5rem] max-[720px]:justify-between max-[720px]:gap-4 max-[720px]:overflow-hidden max-[720px]:rounded-2xl max-[720px]:px-0 max-[720px]:py-[0.65rem] max-[720px]:text-[clamp(2.15rem,11vw,3.5rem)] max-[720px]:leading-[1.05] max-[720px]:font-[650] max-[720px]:tracking-[-0.045em] max-[720px]:text-[var(--ink)] max-[720px]:active:scale-[0.985] max-[720px]:active:bg-[color-mix(in_srgb,var(--ink)_9%,transparent)] fa:tracking-normal"
                key={item.id}
                href={`#${item.id}`}
                onClick={closeMenu}
              >
                <span className="transition-transform duration-200 max-[720px]:group-focus-visible:translate-x-1 rtl:max-[720px]:group-focus-visible:-translate-x-1">{item.label}</span>
                {/* <ArrowUpRight className="hidden max-[720px]:block max-[720px]:size-[1.45rem] max-[720px]:shrink-0 max-[720px]:translate-x-[-0.65rem] max-[720px]:translate-y-[0.65rem] max-[720px]:opacity-25 max-[720px]:transition-[opacity,transform] max-[720px]:duration-200 max-[720px]:group-focus-visible:translate-0 max-[720px]:group-focus-visible:opacity-100 rtl:scale-x-[-1]" aria-hidden="true" /> */}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-[0.35rem] max-[720px]:grid max-[720px]:grid-cols-2 max-[720px]:gap-[0.65rem]" role="group" aria-label={language === "fa" ? "تنظیمات نمایش" : "Display preferences"}>
            <button className="inline-flex min-h-11 min-w-11 items-center justify-center gap-[0.42rem] rounded-full border-0 bg-transparent px-[0.72rem] py-[0.65rem] text-[0.69rem] font-[650] text-[var(--muted)] transition-[color,background-color,transform] duration-150 hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] hover:text-[var(--ink)] active:scale-95 max-[720px]:min-h-[3.6rem] max-[720px]:justify-start max-[720px]:border max-[720px]:border-[var(--line)] max-[720px]:bg-[color-mix(in_srgb,var(--surface)_68%,transparent)] max-[720px]:px-4 max-[720px]:py-[0.9rem] max-[720px]:text-[0.78rem] max-[720px]:text-[var(--ink)] [&_svg]:size-4 max-[720px]:[&_svg]:size-[1.1rem]" type="button" onClick={toggleTheme} aria-label={theme === "dark" ? copy.preferences.light : copy.preferences.dark}>
              {theme === "dark" ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
              <span>{theme === "dark" ? (language === "fa" ? "حالت روشن" : "Light") : (language === "fa" ? "حالت تاریک" : "Dark")}</span>
            </button>
            <button className="inline-flex min-h-11 min-w-11 items-center justify-center gap-[0.42rem] rounded-full border-0 bg-transparent px-[0.72rem] py-[0.65rem] text-[0.69rem] font-[650] text-[var(--muted)] transition-[color,background-color,transform] duration-150 hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] hover:text-[var(--ink)] active:scale-95 max-[720px]:min-h-[3.6rem] max-[720px]:justify-start max-[720px]:border max-[720px]:border-[var(--line)] max-[720px]:bg-[color-mix(in_srgb,var(--surface)_68%,transparent)] max-[720px]:px-4 max-[720px]:py-[0.9rem] max-[720px]:text-[0.78rem] max-[720px]:text-[var(--ink)] [&_svg]:size-4 max-[720px]:[&_svg]:size-[1.1rem]" type="button" onClick={toggleLanguage} aria-label={`${copy.preferences.languageShort} — ${copy.preferences.language}`}>
              <Languages aria-hidden="true" />
              <span>{copy.preferences.languageShort}</span>
            </button>
          </div>
          <a className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-[0.7rem] text-[0.77rem] font-semibold text-[var(--page)] no-underline transition-[transform,background-color] duration-150 hover:bg-[color-mix(in_srgb,var(--ink)_84%,var(--page))] active:scale-[0.97] max-[720px]:min-h-[3.7rem] max-[720px]:justify-center max-[720px]:text-[0.84rem] [&_svg]:w-[0.95rem] rtl:[&_svg]:scale-x-[-1]" href="#contact" onClick={closeMenu}>
            {copy.nav.contact}
            <ArrowUpRight aria-hidden="true" />
          </a>
        </nav>
      </div>
    </header>
  );
}
